import { Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-ftc-qar2018',
    templateUrl: './ftc-qar2018.component.html',
    styleUrls: ['./ftc-qar2018.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FtcQar2018Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private isInitialized = false;
  baseURLs: any = {};
  headerData: any = {};
  rowData: any = {};
  filterParams: any = {};
  GENERAL_CONFIG: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  modalTitle = 'QAR 2018';
  minimise = false;
  basketDetails: any[] = [];
  fdiiDetails: any[] = [];
  QARList: any[] = [];
  total = 0;
  isSaveClicked = false;
  crudLoading = false;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    if (!this.rowData || Object.keys(this.rowData).length === 0) return;
    this.isInitialized = true;
    this.loadCodeMastData();
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  save(): void {
    if (this.crudLoading || this.isSaveClicked) return;
    const activeRows = this.QARList.filter((x: any) => x.OPERATION_TYPE !== 'D');
    if (activeRows.length === 0) return;
    this.crudLoading = true;
    this.isSaveClicked = true;

    const transactionDetails = activeRows.map((row: any) => ({
      COMBINATION_KEY: this.rowData.COMBINATION_KEY,
      SOURCE_KEY: row.SOURCE_KEY,
      FDII_KEY: row.FDII_KEY,
      AMOUNT: row.ADJ_AMT,
      GROUP_KEY: this.rowData.GROUP_KEY,
      ISSUE_ID: this.rowData.ISSUE_ID,
      ISSUE_DESC: this.rowData.ISSUE_DESC,
      COMMENTS: this.rowData.COMMENTS,
      STAGE_KEY: this.rowData.STAGE_KEY,
      ADJUSTMENT_KEY: row.OPERATION_TYPE === 'I' ? null : row.ADJ_KEY,
      IS_SINGLE: 'M',
      OPERATION_TYPE: row.OPERATION_TYPE || 'U'
    }));
    const otherInfo = {
      sso_id: this.USER_SETTINGS?.user?.sso_id || '',
      SCREEN_KEY: this.globalService?.screen_key || this.globalService?.globalParams?.screen_key || '',
      tax_year: this.filterParams?.tax_year || this.rowData?.TAX_YEAR,
      scenario: this.filterParams?.scenario || this.rowData?.SCENARIO,
      jcd_key: this.filterParams?.jcd_key || this.rowData?.JCD_KEY
    };
    const params: any = {
      clob_data: JSON.stringify(transactionDetails),
      clob_settings: JSON.stringify(otherInfo),
      process_name: 'QAR_SAVE',
      ...this.globalService,
      ...this.filterParams
    };
    this.postForm('/saveJsonObject?action_code=eofqqx', params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      () => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.requestGridUpdate.emit('refresh');
        this.cdr.detectChanges();
      },
      () => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.cdr.detectChanges();
      }
    );
  }

  addRow(): void {
    this.QARList.push({ SOURCE_KEY: 0, FDII_KEY: 0, ADJ_AMT: '', OPERATION_TYPE: 'I' });
    this.updateTotal();
  }

  deleteRow(row: any): void {
    const idx = this.QARList.indexOf(row);
    if (idx < 0) return;
    if (this.QARList[idx].OPERATION_TYPE === 'I') {
      this.QARList.splice(idx, 1);
    } else {
      this.QARList[idx].OPERATION_TYPE = 'D';
    }
    this.updateTotal();
  }

  reset(): void {
    this.QARList = [];
    this.loadCodeMastData();
  }

  updateTotal(): void {
    this.total = this.QARList
      .filter((x: any) => x.OPERATION_TYPE !== 'D')
      .reduce((sum: number, x: any) => sum + (Number(x.ADJ_AMT) || 0), 0);
  }

  private loadCodeMastData(): void {
    const params1 = {
      action_code: 'dbty56',
      p_scenario: this.filterParams?.scenario || this.rowData?.SCENARIO,
      p_jcd_key: this.filterParams?.jcd_key || this.rowData?.JCD_KEY || 250,
      cc_key: this.rowData?.CODE_COMBINATION
    };
    this.postForm('/loadJsonObject', params1).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      const list = Array.isArray(data?.jsonObject) ? data.jsonObject : [];
      this.basketDetails = list.filter((x: any) => x.CODE_GROUP === 'SOURCE_CODE');
      this.fdiiDetails = list.filter((x: any) => x.CODE_GROUP === 'FDII_CODE');
      this.cdr.detectChanges();
    });

    const params2 = {
      action_code: 'yenzpg',
      p_tax_year: this.filterParams?.tax_year || this.rowData?.TAX_YEAR,
      p_scenario: this.filterParams?.scenario || this.rowData?.SCENARIO,
      p_jcd_key: this.filterParams?.jcd_key || this.rowData?.JCD_KEY || 250,
      adj_key: this.rowData?.ADJ_KEY,
      group_key: this.rowData?.GROUP_KEY
    };
    this.postForm('/loadJsonObject', params2).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.QARList = (Array.isArray(data?.jsonObject) ? data.jsonObject : []).map((x: any) => ({ ...x, OPERATION_TYPE: 'U' }));
      if (this.QARList.length === 0) this.addRow();
      this.updateTotal();
      this.cdr.detectChanges();
    });
  }

  private postForm(endpoint: string, params: any) {
    const body = new URLSearchParams();
    Object.keys(params || {}).forEach((k) => {
      if (params[k] !== undefined && params[k] !== null) {
        body.set(k, String(params[k]));
      }
    });
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    return this.http.post<any>(`${this.GENERAL_CONFIG.base_url}${endpoint}`, body.toString(), { headers, withCredentials: true });
  }
}
